/**
 *
 * OktaVerificationLayout
 *
 */

import React from "react";
import classnames from "classnames";
import { stylize } from "@envestnet/envreact-component-library";
import withIntl from "utils/withIntl";
import { useLocation } from "react-router-dom";

import CentricLayout from "components/CentricLayout";

import DashboardMfaLayout from "components/DashboardMfaLayout";
import { mfaFlow } from "utils/enum";
import styles, { Styles } from "./styles";
import messages from "./messages";
import { AuthenticatorStatusEnum, PassCodeSubmissionStatusEnum } from "./enums";
import { StatusType, SubmissionStateType } from "./types";
import OktaAuth from "./OktaAuth";

interface InjectedProps extends Styles {
  /** Internal - injected from withIntl */
  formatMessage: FormatMessage;

  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;
}

interface DefaultProps {
  // Default props should always be required props, i.e. no ? indicating it's optional
  // The default values are specified below

  /** Description of this prop */
  loadQRCode: Function;
  loadStatus: Function;
  submitPassCode: Function;
  goToMFAScreen: Function;
  resetSubmissionStatus: Function;

  loadingPassCodeSubmission: SubmissionStateType;
  qrCodeLink: string;
  status: StatusType;
  secretCode: string;
  username: string;
}

export interface OktaVerificationLayoutProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;
}

const defaultProps: DefaultProps = {
  loadQRCode: () => {},
  loadStatus: () => {},
  submitPassCode: () => {},
  goToMFAScreen: () => {},
  resetSubmissionStatus: () => {},
  qrCodeLink: "",
  status: AuthenticatorStatusEnum.INITIAL,
  loadingPassCodeSubmission: PassCodeSubmissionStatusEnum.INITIATE,
  secretCode: "",
  username: "",
};

type Props = InjectedProps & OktaVerificationLayoutProps;

const OktaVerificationLayout = (props: Props) => {
  const {
    classes,
    style,
    className,
    formatMessage,
    innerRef,
    goToMFAScreen,
    loadQRCode,
    qrCodeLink,
    loadStatus,
    status,
    submitPassCode,
    loadingPassCodeSubmission,
    resetSubmissionStatus,
    secretCode,
    username,
  } = props;
  const [activeStep, setActiveStep] = React.useState(1);
  const { state } = useLocation();

  const redirectToMfa = () => {
    goToMFAScreen(state.navigationFrom);
  };

  React.useEffect(() => {
    if (loadingPassCodeSubmission === PassCodeSubmissionStatusEnum.DONE) {
      redirectToMfa();
    }
  }, [loadingPassCodeSubmission]);

  React.useEffect(() => {
    if (status === AuthenticatorStatusEnum.PENDING) setActiveStep(2);
    else if (status === AuthenticatorStatusEnum.VERIFIED) redirectToMfa();
  }, [status]);

  return state?.currentLayout === mfaFlow.MFA ? (
    // @ts-ignore
    <DashboardMfaLayout>
      <CentricLayout className={classes.mfaCenter}>
        <OktaAuth
          hideLogo
          secretCode={secretCode}
          resetSubmissionStatus={resetSubmissionStatus}
          username={username}
          loadQRCode={loadQRCode}
          currentLayout={state?.currentLayout}
          qrCodeLink={qrCodeLink}
          submitPassCode={submitPassCode}
          loadingPassCodeSubmission={loadingPassCodeSubmission}
          loadStatus={loadStatus}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          redirectToMfa={redirectToMfa}
          authenticatorName={formatMessage(messages.header)}
        />
      </CentricLayout>
    </DashboardMfaLayout>
  ) : (
    <div
      ref={innerRef}
      className={classnames(className, classes.container)}
      style={style}
    >
      <CentricLayout>
        <div>
          <OktaAuth
            hideLogo={false}
            secretCode={secretCode}
            resetSubmissionStatus={resetSubmissionStatus}
            username={username}
            loadQRCode={loadQRCode}
            qrCodeLink={qrCodeLink}
            submitPassCode={submitPassCode}
            loadingPassCodeSubmission={loadingPassCodeSubmission}
            currentLayout={state?.currentLayout}
            loadStatus={loadStatus}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            redirectToMfa={redirectToMfa}
            authenticatorName={formatMessage(messages.header)}
          />
        </div>
      </CentricLayout>
    </div>
  );
};

OktaVerificationLayout.defaultProps = defaultProps;

export default withIntl(
  stylize<Props, DefaultProps>(styles)(OktaVerificationLayout)
);
